/**
 * Home Desk Tea ship-window countdown (Europe/Amsterdam).
 * Display only — never promises a delivery date.
 *
 * Model (Michalis):
 * - Weekday order (even evening) → ships next working day.
 * - Weekend/holiday order → ships next working day (usually Monday).
 * - Cut-off default 23:59: before → countdown; after → counts as next day's order.
 */

const TZ = 'Europe/Amsterdam';
const DAY_KEYS = ['', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * @param {Date} date
 */
export function amsParts(date) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  const map = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  const wd = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 }[map.weekday] || 1;
  return {
    y: Number(map.year),
    m: Number(map.month),
    d: Number(map.day),
    wd,
    hour: Number(map.hour),
    minute: Number(map.minute),
    second: Number(map.second),
    iso: `${map.year}-${map.month}-${map.day}`,
  };
}

function iso(y, m, d) {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function addDays(p, days) {
  const utc = new Date(Date.UTC(p.y, p.m - 1, p.d + days));
  return { y: utc.getUTCFullYear(), m: utc.getUTCMonth() + 1, d: utc.getUTCDate() };
}

function weekdayOf(p) {
  const js = new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
  return js === 0 ? 7 : js;
}

/**
 * @param {object} opts
 * @param {Date} now
 * @param {Record<string,string>} strings
 */
export function computeShipWindow(now, opts = {}, strings = {}) {
  const cutoff = opts.cutoff || '23:59';
  const shipDays = (opts.shipDays || '1,2,3,4,5')
    .split(',')
    .map((s) => Number(s.trim()))
    .filter((n) => n >= 1 && n <= 7);
  const holidays = new Set(
    (opts.holidays || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );

  const isHoliday = (p) => holidays.has(iso(p.y, p.m, p.d));
  const isShipDay = (p) => !isHoliday(p) && shipDays.includes(weekdayOf(p));
  const isNonOrderDay = (p) => !isShipDay(p); // weekend or holiday

  /** Next working ship day strictly after p. */
  const nextWorkingAfter = (p) => {
    let cur = addDays(p, 1);
    for (let i = 0; i < 21; i++) {
      if (isShipDay(cur)) return cur;
      cur = addDays(cur, 1);
    }
    return cur;
  };

  /** First ship day on or after p. */
  const firstShipOnOrAfter = (p) => {
    let cur = { ...p };
    for (let i = 0; i < 21; i++) {
      if (isShipDay(cur)) return cur;
      cur = addDays(cur, 1);
    }
    return cur;
  };

  const [ch, cm] = cutoff.split(':').map((x) => Number(x));
  const co = { h: Number.isFinite(ch) ? ch : 23, m: Number.isFinite(cm) ? cm : 59 };

  const cutoffUtcMs = (day) => {
    const targetMin = co.h * 60 + co.m;
    let lo = Date.UTC(day.y, day.m - 1, day.d - 1, 0, 0, 0);
    let hi = Date.UTC(day.y, day.m - 1, day.d + 2, 23, 59, 59);
    for (let i = 0; i < 45; i++) {
      const mid = Math.floor((lo + hi) / 2);
      const parts = amsParts(new Date(mid));
      const mins = parts.hour * 60 + parts.minute;
      const dayCmp =
        parts.y !== day.y || parts.m !== day.m || parts.d !== day.d
          ? parts.iso < iso(day.y, day.m, day.d)
            ? -1
            : 1
          : 0;
      if (dayCmp < 0 || (dayCmp === 0 && mins < targetMin)) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  };

  const parts = amsParts(now);
  const today = { y: parts.y, m: parts.m, d: parts.d };
  const nowMins = parts.hour * 60 + parts.minute;
  const nowSecs = nowMins * 60 + parts.second;
  const cutoffSecs = co.h * 60 * 60 + co.m * 60; // cut-off at HH:MM:00
  const beforeCutoff = nowSecs < cutoffSecs;

  let ship;
  let mode;
  let remainingMs = 0;

  if (isNonOrderDay(today)) {
    // Weekend / holiday → ships next working day (Sat/Sun → Monday; holiday → next ship day)
    ship = firstShipOnOrAfter(today);
    mode = 'later';
  } else if (beforeCutoff) {
    // Weekday before cut-off → ships next working day; countdown to cut-off
    ship = nextWorkingAfter(today);
    mode = 'countdown';
    remainingMs = Math.max(0, cutoffUtcMs(today) - now.getTime());
  } else {
    // Weekday after cut-off → counts as next day's order
    const nextDay = addDays(today, 1);
    if (isNonOrderDay(nextDay)) {
      // Fri night → Sat → weekend → Monday
      ship = firstShipOnOrAfter(nextDay);
    } else {
      // Mon night → Tue's order → ships Wed
      ship = nextWorkingAfter(nextDay);
    }
    mode = 'later';
  }

  const tomorrow = addDays(today, 1);
  const sameTomorrow = ship.y === tomorrow.y && ship.m === tomorrow.m && ship.d === tomorrow.d;
  const dayKey = DAY_KEYS[weekdayOf(ship)];
  // "tomorrow" only in weekday countdown; weekends always say the weekday (e.g. Monday).
  const label =
    mode === 'countdown' && sameTomorrow
      ? strings.tomorrow || 'tomorrow'
      : strings[dayKey] || dayKey;

  let text;
  if (mode === 'countdown') {
    const totalSec = Math.floor(remainingMs / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    // Under 1 h: minutes only. At least 1 min if any time left.
    const timeStr =
      h >= 1
        ? (strings.hours_mins || '{{ hours }} h {{ minutes }} min')
            .replace('{{ hours }}', String(h))
            .replace('{{ minutes }}', String(m))
        : (strings.mins || '{{ minutes }} min').replace(
            '{{ minutes }}',
            String(totalSec > 0 ? Math.max(1, m) : 0)
          );
    text = (strings.countdown || 'Order in the next {{ time }} → ships {{ day }}')
      .replace('{{ time }}', timeStr)
      .replace('{{ day }}', label);
  } else {
    text = (strings.later || 'Order now → ships {{ day }}').replace('{{ day }}', label);
  }

  return { mode, text, label, remainingMs, shipIso: iso(ship.y, ship.m, ship.d) };
}

/**
 * @param {HTMLElement} root
 */
export function initShipCountdown(root) {
  if (!(root instanceof HTMLElement)) return;
  const out = root.querySelector('[data-ht-ship-countdown-text]');
  if (!out) return;

  const stringsEl = root.querySelector('[data-ht-ship-strings]');
  let strings = {};
  try {
    strings = JSON.parse(stringsEl?.textContent || '{}');
  } catch {
    strings = {};
  }

  const opts = {
    cutoff: root.dataset.cutoff || '23:59',
    shipDays: root.dataset.shipDays || '1,2,3,4,5',
    holidays: root.dataset.holidays || '',
  };

  const tick = () => {
    const result = computeShipWindow(new Date(), opts, strings);
    out.textContent = result.text;
    root.hidden = false;
    root.setAttribute('data-ht-ship-mode', result.mode);
  };

  root._htShipCompute = (d) => computeShipWindow(d, opts, strings);
  tick();
  const id = window.setInterval(tick, 30000);
  root.addEventListener('ht:ship-countdown-stop', () => window.clearInterval(id), { once: true });
}

function boot() {
  document.querySelectorAll('[data-ht-ship-countdown]').forEach((el) => {
    if (el instanceof HTMLElement && !el.hasAttribute('data-ht-ship-ready')) {
      el.setAttribute('data-ht-ship-ready', '');
      initShipCountdown(el);
    }
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
}
